import type { SupabaseClient } from "@supabase/supabase-js";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { createManagementReportTransport } from "../data/management-report-snapshot";
import { useSupportSession } from "../support/SupportSessionProvider";
import { ReportSnapshotWorkspace } from "./ReportSnapshotWorkspace";

// Deliberately source-disabled. No runtime config, environment or URL override.
// Enable only in a separately reviewed release after native/hosted qualification.
export const REPORT_SNAPSHOT_SEARCH_ENABLED: boolean = false;

type Props = {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  accountRole: string | null;
  can: (permission: string) => boolean;
};

/** Disabled admission does not subscribe to auth, construct a transport or issue an RPC. */
export function ReportSnapshotAdmission(props: Props) {
  return REPORT_SNAPSHOT_SEARCH_ENABLED ? <ReportSnapshotScope {...props} /> : null;
}

/** Exported for direct synthetic qualification; the production entry is Admission above. */
export function ReportSnapshotScope({ client, accountId, timeZone, accountRole, can }: Props) {
  const auth = useAuth();
  const support = useSupportSession();
  const lease = useRef<AbortController | null>(null);
  const [boundary, setBoundary] = useState<{ client: SupabaseClient | null; epoch: number; ready: boolean; signal: AbortSignal | null }>({
    client: null, epoch: 0, ready: false, signal: null,
  });

  useLayoutEffect(() => {
    let active = true;
    // A replacement client's previous hook state is not evidence about its session.
    lease.current?.abort();
    const initial = new AbortController(); lease.current = initial;
    setBoundary((previous) => ({ client, epoch: previous.epoch + 1, ready: false, signal: initial.signal }));
    const { data } = client.auth.onAuthStateChange((event) => {
      // Event only: never inspect, decode, copy or retain the session/token payload.
      if (!active) return;
      // Revoke the old lease synchronously, before React may batch the remount.
      // A pending validation promise must not release a CSV in that gap.
      lease.current?.abort();
      const next = new AbortController(); lease.current = next;
      setBoundary((previous) => ({
        client,
        epoch: previous.epoch + 1,
        ready: event !== "SIGNED_OUT" && event !== "PASSWORD_RECOVERY",
        signal: next.signal,
      }));
    });
    return () => { active = false; lease.current?.abort(); data.subscription.unsubscribe(); };
  }, [client]);

  const admitted = boundary.client === client && boundary.ready && boundary.signal !== null && !boundary.signal.aborted
    && auth.status === "authenticated" && Boolean(auth.user?.id) && auth.recoveryMode !== true
    && (accountRole === "admin" || accountRole === "supervisor")
    && can("report.view") && can("evidence.view")
    && !support.loading && support.session === null;
  // An auth event changes transport identity as well as the React key. Old callbacks
  // are fenced by Workspace even if the same actor/account survives a token refresh.
  const transport = useMemo(() => admitted ? createManagementReportTransport(client) : null,
    [client, boundary.epoch, admitted]);
  if (!admitted || transport === null) return null;
  const key = JSON.stringify([auth.user!.id, accountId, timeZone, accountRole, boundary.epoch]);
  return <ReportSnapshotWorkspace key={key} accountId={accountId} timeZone={timeZone} transport={transport} authorizationSignal={boundary.signal!} />;
}
